import React, { useEffect, useMemo, useState } from 'react';
import InlineTemplatePicker from './InlineTemplatePicker';
import ConversationPeoplePanel from './ConversationPeoplePanel';
import { formatRelativeAge } from '../../lib/communicationsPresence';
import { buildTemplateVariableValues } from '../../lib/communicationsTemplates';
import { buildBaseTemplateContext, loadTemplateContext } from '../../lib/templateContextLoader';
import { getTemplateDisplayName } from '../../lib/communicationsTemplates';
import { formatPhone } from '../../lib/phone';

const MessageTemplatesSettingsView = React.lazy(() => import('../settings/MessageTemplatesSettingsView'));
const TABS = [
    ['ai_activity', 'AI Activity'],
    ['templates', 'Templates'],
    ['contact', 'Contact'],
    ['trip', 'Trip'],
];

function stamp(value) {
    const parsed = new Date(value || 0);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleString();
}

function aiTimeline(session = {}) {
    const entries = Array.isArray(session?.conversation_log) ? session.conversation_log : [];
    const mapped = entries
        .filter((entry) => ['assistant', 'staff'].includes(entry?.role))
        .map((entry, index) => ({
            id: `ai-${index}`,
            title: entry.role === 'assistant' ? 'AI sent a reply' : 'Staff handoff',
            detail: entry.content || '',
            at: entry.at || null,
        }));
    if (session?.ai_paused_until) {
        mapped.push({
            id: 'ai-paused',
            title: 'AI paused',
            detail: `Resumes ${stamp(session.ai_paused_until) || 'when conversation closes'}`,
            at: session.ai_paused_until,
        });
    }
    return mapped.sort((a, b) => new Date(b.at || 0).getTime() - new Date(a.at || 0).getTime());
}

function CommunicationsThreadSidePanel({
    open = false,
    pinned = false,
    onToggleOpen,
    onTogglePinned,
    activeTab = 'ai_activity',
    onTabChange,
    selectedChannel = '',
    onWhatsAppFlagChange,
    theme = 'light',
    intakeSession,
    contact = null,
    contactName = '',
    address = '',
    templates = [],
    templatePanelMode = 'more',
    templatePanelChannel = '',
    client,
    activeSendingIdentity = null,
    selectedTemplateSid = '',
    templateVariables = {},
    onTemplateSelect,
    onTemplateVariableChange,
    // BL-177 Step 3: the New message panel shows the template's values once, in its own card; the side panel then leaves them out.
    hideTemplateFillIns = false,
    currentUserId = null,
    // BL-177 Step 2l: the People tab, only for a conversation the signed-in person is in ({ people, loading, error, onInvite, onLeave, busy }).
    peopleTab = null,
    // Comms final pass: Settings → Templates links are admin-only.
    canEditTemplates = true,
    // Comms gate fixes: the brand this conversation / message is sent for (a template's "Company name").
    templateCompanyName = '',
}) {
    const dark = theme === 'dark';
    const timeline = useMemo(() => aiTimeline(intakeSession), [intakeSession]);
    const [templateContext, setTemplateContext] = useState(() => buildBaseTemplateContext({
        session: intakeSession,
        contact,
        contactName,
    }));
    const activeChannel = selectedChannel || activeSendingIdentity?.channel || intakeSession?.channel || '';
    const activeTwilioAccountSid = activeSendingIdentity?.twilioAccountSid || null;
    const approvedTemplates = useMemo(() => templates.filter((template) => (
        template?.status === 'approved'
        && !template?.archived_at
        && (!activeChannel || !template?.channel || template.channel === activeChannel)
        && (
            template?.channel !== 'whatsapp'
            || (template?.twilioAccountSid || null) === activeTwilioAccountSid
        )
    )), [activeChannel, activeTwilioAccountSid, templates]);
    const recentTemplateSids = useMemo(() => {
        const sidByContentSid = new Map(
            approvedTemplates
                .filter((template) => template?.contentSid)
                .map((template) => [template.contentSid, template.sid])
        );
        const outbound = (Array.isArray(intakeSession?.raw_message_log) ? intakeSession.raw_message_log : [])
            .filter((entry) => entry?.direction === 'outbound' && typeof entry?.contentSid === 'string' && entry.contentSid.trim())
            .sort((left, right) => new Date(right.at || right.sent_at || 0).getTime() - new Date(left.at || left.sent_at || 0).getTime());
        return [...new Set(outbound.map((entry) => sidByContentSid.get(entry.contentSid.trim()) || ''))].filter(Boolean);
    }, [approvedTemplates, intakeSession?.raw_message_log]);
    useEffect(() => {
        let active = true;
        const base = buildBaseTemplateContext({
            session: intakeSession,
            contact,
            contactName,
        });
        setTemplateContext(base);
        loadTemplateContext({
            session: intakeSession,
            contact,
            contactName,
        }).then((next) => {
            if (active) setTemplateContext(next);
        }).catch(() => {});
        return () => { active = false; };
    }, [intakeSession, contact, contactName]);

    const panelCls = dark ? 'border-gray-700 bg-gray-900/70 text-gray-100' : 'border-gray-200 bg-white text-gray-800';
    if (!open) {
        return (
            <aside className={`ia-comms-sidepanel-rail ml-2 shrink-0 rounded-xl border ${panelCls}`} style={{ width: 44 }} data-side-panel="closed">
                <button
                    type="button"
                    onClick={onToggleOpen}
                    aria-label="Open side panel"
                    className="flex h-full w-full items-start justify-center pt-3 text-sm"
                >
                    »
                </button>
            </aside>
        );
    }

    return (
        // BL-177 Step 2d: below 1200px the open panel is a drawer over the thread/compose column (never wider than it);
        // from 1200px it sits beside the thread at 390px as before.
        <aside className={`ia-comms-sidepanel shrink-0 rounded-xl border ${panelCls}`} data-side-panel="open" aria-label="Conversation details">
            <div className={`flex flex-wrap items-center justify-between gap-1 border-b px-3 py-2 ${dark ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex flex-wrap gap-1" role="tablist" aria-label="Thread side panel tabs">
                    {(peopleTab ? [['people', 'People'], ...TABS] : TABS).map(([id, label]) => (
                        <button
                            key={id}
                            type="button"
                            role="tab"
                            aria-selected={activeTab === id}
                            onClick={() => onTabChange?.(id)}
                            className={`rounded px-2 py-1 text-xs ${activeTab === id ? 'bg-[#03B7C9]/20 text-[#03B7C9]' : (dark ? 'text-gray-400' : 'text-gray-500')}`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-2">
                    <button type="button" onClick={onTogglePinned} className="text-xs underline" aria-label="Keep panel open">
                        {pinned ? 'Release panel' : 'Keep panel open'}
                    </button>
                    <button type="button" onClick={onToggleOpen} aria-label="Collapse side panel" className="ia-sidepanel-close text-xs">
                        <span aria-hidden="true">«</span>
                        {/* phones: the panel is its own screen, so the way out is a labelled "← Back" (see styles/step2n.css) */}
                        <span className="ia-sidepanel-close__phone">← Back</span>
                    </button>
                </div>
            </div>
            {/* Comms polish hotfix: the body fills what the (sometimes two-row) header leaves, so its end is never cut off. */}
            <div className="ia-comms-sidepanel__body h-[calc(100%-40px)] overflow-auto p-3 text-xs" data-side-panel-body="true">
                {activeTab === 'people' && peopleTab && (
                    <ConversationPeoplePanel
                        people={peopleTab.people}
                        loading={peopleTab.loading}
                        error={peopleTab.error}
                        onInvite={peopleTab.onInvite}
                        onLeave={peopleTab.onLeave}
                        busy={peopleTab.busy}
                        pendingInvite={peopleTab.pendingInvite}
                        onWithdraw={peopleTab.onWithdraw}
                    />
                )}
                {activeTab === 'ai_activity' && (
                    <div className="space-y-2">
                        {timeline.length === 0 ? (
                            <p className={dark ? 'text-gray-400' : 'text-gray-500'}>No AI activity yet.</p>
                        ) : timeline.map((item) => (
                            <div key={item.id} className={`rounded border p-2 ${dark ? 'border-gray-700' : 'border-gray-200'}`}>
                                <p className="font-medium">{item.title}</p>
                                {item.detail && <p className={`mt-1 whitespace-pre-wrap ${dark ? 'text-gray-300' : 'text-gray-600'}`}>{item.detail}</p>}
                                {item.at && <p className={`mt-1 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>{stamp(item.at)} · {formatRelativeAge(item.at)}</p>}
                            </div>
                        ))}
                    </div>
                )}
                {activeTab === 'templates' && templatePanelMode === 'edit' && canEditTemplates ? <React.Suspense fallback={<p>Loading template editor…</p>}><MessageTemplatesSettingsView key={templatePanelChannel} initialChannel={templatePanelChannel || activeChannel || 'sms'} embedded client={client} orgId={intakeSession?.org_id} theme={theme} /></React.Suspense> : activeTab === 'templates' && (
                    <InlineTemplatePicker
                        templates={approvedTemplates}
                        selectedSid={selectedTemplateSid}
                        variables={templateVariables}
                        userId={currentUserId}
                        orgId={intakeSession?.org_id || null}
                        recentSids={recentTemplateSids}
                        channel={activeChannel}
                        theme={theme}
                        canEditTemplates={canEditTemplates}
                        onSelect={(template) => onTemplateSelect?.(
                            template.sid,
                            buildTemplateVariableValues(template, { ...templateContext, companyName: templateCompanyName }, { useSampleFallback: template?.channel !== 'sms' })
                        )}
                        onVariableChange={onTemplateVariableChange}
                        hideFillIns={hideTemplateFillIns}
                    />
                )}
                {activeTab === 'contact' && (
                    <div className="space-y-2">
                        <p className="font-semibold">{contact?.name || contactName}</p>
                        {!contact && <p className="inline-block rounded-full bg-amber-100 px-2 py-0.5 text-amber-800">New · not in CRM</p>}
                        <p>{[...new Set([...(contact?.phones || []).map((phone) => formatPhone(typeof phone === 'string' ? phone : phone?.number || phone?.phone || phone?.value || '')), ...(contact?.emails || []), formatPhone(address)].filter(Boolean))].join(' · ') || 'No contact details'}</p>
                        {contact?.phones?.map((phone, index) => {
                            const number = typeof phone === 'string' ? phone : phone?.number || phone?.phone || phone?.value;
                            if (!number) return null;
                            return (
                                <label key={`${number}-${index}`} className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={Boolean(phone?.whatsapp)}
                                        onChange={(event) => onWhatsAppFlagChange?.(phone, index, event.target.checked)}
                                        aria-label={`On WhatsApp: ${formatPhone(number)}`}
                                    />
                                    On WhatsApp · {formatPhone(number)}
                                </label>
                            );
                        })}
                        {contact?.sms_opted_out_at && <p className="text-[#FCA5A5]">Opted out of texts</p>}
                        {contact?.id && <a href="/clients" className="text-[#02808D] underline">Open in CRM</a>}
                    </div>
                )}
                {activeTab === 'trip' && (
                    <div>
                        <p className="font-semibold">Trip details</p>
                        <p className="mt-1 whitespace-pre-wrap">{intakeSession?.itinerary_short_form || 'No trip details on file.'}</p>
                        {intakeSession?.client_tier && <p className="mt-1">Client tier: {intakeSession.client_tier}</p>}
                        {Number.isFinite(intakeSession?.pass_count) && Number.isFinite(intakeSession?.pass_cap) && (
                            <p className="mt-1">Pass count {intakeSession.pass_count}/{intakeSession.pass_cap}</p>
                        )}
                    </div>
                )}
            </div>
        </aside>
    );
}

export default CommunicationsThreadSidePanel;
